import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  assistantAdminSettings,
  assistantAdminSettingsSave,
  atlasAutonomy,
  type ApiError,
  type AssistantAdminSettings,
} from '@aithora/shared';
import type { QueryState } from '../../useQuery';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, LoadError, pre, sx, useApi, useGuarded } from './kit';
import { AboutThis } from '../../ui/AboutThis';
import { passkeyStepUp } from './stepUp';
import { AssistantCopilotTab, AssistantRulesTab, AssistantUseTab } from './AssistantTabs';

// Settings, Atlas Assistant (Console V5; design T10304-10600, values V5 L37700-37880): four
// tabs. How Atlas behaves (Spec V4 16 G): the owner (Andrew, by email) edits, the Operator
// tier reads; every change but Back to Claude needs a reason and a passkey, and is sealed to
// the Truth Ledger by the server. Rules and schedules, Use and spend, and the Chrome copilot
// design are in AssistantTabs.tsx. The Aithora team only (lens.ts; the server refuses
// everyone else). Crisis and medical routing is never changed from here.

const chip = (c: string, b: string) => 'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;white-space:nowrap;color:' + c + ';background:' + b + ';';
const CAP = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const ROW = 'display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap;row-gap:6px;padding:10px 2px;border-top:1px solid rgba(34,39,46,.1);';
const BTN = 'display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;border:1px solid rgba(34,39,46,.2);color:#22272E;';
const radio = (on: boolean) => 'flex:0 0 16px;width:16px;height:16px;margin-top:2px;border-radius:50%;box-sizing:border-box;' + (on ? 'border:5px solid #264C7E;' : 'border:1.5px solid rgba(34,39,46,.3);');
const pill = (on: boolean, dis: boolean) => 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (on ? 'background:#264C7E;color:#fff;' : dis ? 'border:1px solid rgba(34,39,46,.1);color:#54606C;cursor:default;' : 'border:1px solid rgba(34,39,46,.18);color:#3C454F;');
const track = (on: boolean, dis: boolean) => 'flex:0 0 40px;width:40px;height:24px;border-radius:12px;position:relative;display:block;margin-top:2px;' + (on ? 'background:#264C7E;' : 'background:rgba(34,39,46,.18);') + (dis ? 'opacity:.55;cursor:default;' : '');
const knob = (on: boolean) => 'position:absolute;top:3px;left:' + (on ? 19 : 3) + 'px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 2px rgba(34,39,46,.25);';

type Key = 'model' | 'shadow' | 'chatConfirm' | 'teamFreeform' | 'teamAnswersWhatsApp';
interface Pending { key: Key; value: string | boolean; title: string }

const MODELS: { key: 'anthropic' | 'local' | 'mistral'; name: string; note: string }[] = [
  { key: 'anthropic', name: 'Claude', note: 'Answers every question today, including every crisis and medical question.' },
  { key: 'local', name: 'Our own model (Gemma)', note: 'Runs on our own server, inside our BAA. Locked until it passes the exam against Claude with zero critical failures, then 2 weeks in shadow.' },
  { key: 'mistral', name: 'Mistral (hosted)', note: 'Same exam and shadow rule. Its hosted service never sees resident information; those questions still go to Claude.' },
];
const SHADOW: [('off' | 'local' | 'mistral'), string][] = [['off', 'Off'], ['local', 'Our own model'], ['mistral', 'Mistral']];
const LIMIT_LABEL: Record<string, string> = { yes_window: 'Time to reply YES to an approval text', pin_tries: 'Wrong PIN tries before it locks', per_person_caps: 'AI spend per person per day' };
const label = (key: string, v: string): string =>
  key === 'model' ? 'Model: ' + (MODELS.find((m) => m.key === v)?.name ?? v)
    : key === 'shadow' ? 'Shadow model: ' + (v === 'off' ? 'Off' : v === 'local' ? 'Our own model' : 'Mistral')
      : key === 'chatConfirm' ? 'Chat confirmation: ' + (v === 'true' ? 'On' : 'Off')
        : key === 'teamAnswersWhatsApp' || key === 'channels_team_answers_whatsapp' ? 'Team answers on WhatsApp: ' + (v === 'true' ? 'On' : 'Off')
          : 'Team answers by text: ' + (v === 'true' ? 'On' : 'Off');

type Tab = 'how' | 'rules' | 'use' | 'cp';
const TABS: [Tab, string][] = [['how', 'How Atlas behaves'], ['rules', 'Rules and schedules'], ['use', 'Use and spend'], ['cp', 'Chrome copilot']];
const tabSt = (on: boolean) => 'display:inline-flex;align-items:center;height:34px;font-size:16px;font-weight:' + (on ? '700;color:#22272E;border-bottom:2px solid #264C7E;' : '600;color:#54606C;border-bottom:2px solid transparent;') + 'text-decoration:none;white-space:nowrap;';

export function AssistantSection() {
  const q = useApi((c, s) => assistantAdminSettings(c, { signal: s }), []);
  const [tab, setTab] = useState<Tab>('how');
  const canEdit = !!q.data?.canEdit;
  return (
    <div data-screen-label="Assistant settings" data-testid="settings-assistant" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx(CAP)}>Assistant · how Atlas behaves</span>
        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
        <span style={sx(canEdit ? chip('#264C7E', 'rgba(47,92,151,.12)') : chip('#54606C', 'rgba(34,39,46,.08)'))}>{canEdit ? 'OWNER ONLY' : 'READ ONLY'}</span>
      </div>
      <div role="tablist" aria-label="Atlas Assistant" style={sx('display:flex;gap:18px;border-bottom:1px solid rgba(34,39,46,.12);margin:-4px 0 12px;')}>
        {TABS.map(([k, n]) => (
          <a key={k} href="#" role="tab" aria-selected={tab === k} onClick={(e) => { pre(e); setTab(k); }} style={sx(tabSt(tab === k))}>{n}</a>
        ))}
      </div>
      {tab === 'how' && <HowTab q={q} />}
      {tab === 'rules' && <AssistantRulesTab />}
      {tab === 'use' && <AssistantUseTab owner={canEdit} />}
      {tab === 'cp' && <AssistantCopilotTab />}
    </div>
  );
}

function HowTab({ q }: { q: QueryState<AssistantAdminSettings> }) {
  const { client } = useSession();
  const navigate = useNavigate();
  const { roReason } = useGuarded();
  const auto = useApi((c, s) => atlasAutonomy(c, { signal: s }), []);
  const [pend, setPend] = useState<Pending | null>(null);
  const [why, setWhy] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);

  if (!q.data) return q.error ? <LoadError error={q.error} onRetry={q.reload} /> : <Skeleton rows={6} label="Loading the assistant settings" />;
  const d: AssistantAdminSettings = q.data;
  const owner = d.canEdit && roReason === null;
  const ask = (key: Key, value: string | boolean, title: string) => (e: MouseEvent) => {
    pre(e);
    if (!owner) return;
    setErr(null);
    setPend({ key, value, title });
    setWhy('');
  };
  const save = async (p: Pending, reason: string | null) => {
    setErr(null);
    try {
      // Back to Claude is one tap with no gate; every other change carries a fresh passkey.
      const back = p.key === 'model' && p.value === 'anthropic';
      const stepUpToken = back ? undefined : await passkeyStepUp(client);
      const r = await assistantAdminSettingsSave(client, { body: { key: p.key, value: p.value, ...(reason ? { reason } : {}), ...(stepUpToken ? { stepUpToken } : {}) } });
      q.setData(() => r);
      setPend(null);
      setWhy('');
    } catch (e) {
      setErr(e instanceof Error ? ({ message: e.message } as ApiError) : (e as ApiError));
    }
  };
  const cand = (k: string) => d.candidates.find((c) => c.key === k);
  const level = auto.data?.autonomy?.level ?? null;
  const toggles: { key: Key; name: string; note: string; on: boolean; tag: [string, string] }[] = [
    {
      key: 'chatConfirm', name: 'Chat confirmation', on: d.chatConfirm,
      note: 'A typed "yes" in chat lets Atlas run a gated action. We recommend leaving this off and retiring it: approval cards with a passkey do the same job more safely.',
      tag: d.chatConfirm ? ['ON', chip('#8A1C16', 'rgba(179,38,30,.1)')] : ['OFF · RECOMMENDED', chip('#12503C', 'rgba(31,107,82,.14)')],
    },
    {
      key: 'teamFreeform', name: 'Team answers by text', on: d.teamAnswersText ?? d.teamFreeform,
      note: 'Lets the Aithora team ask Atlas free-form questions by SMS, not just approve and pause. Every answer passes the health-information check first. Off until decided.',
      tag: (d.teamAnswersText ?? d.teamFreeform) ? ['ON', chip('#264C7E', 'rgba(47,92,151,.12)')] : ['OFF', chip('#54606C', 'rgba(34,39,46,.08)')],
    },
    {
      // An older server has one switch for both; this one reads off until it sends its own.
      key: 'teamAnswersWhatsApp', name: 'Team answers on WhatsApp', on: !!d.teamAnswersWhatsApp,
      note: 'Same, on WhatsApp. WhatsApp is not covered by a BAA, so we recommend leaving it off until the health-information check has run clean on text for 30 days.',
      tag: d.teamAnswersWhatsApp ? ['ON', chip('#8A1C16', 'rgba(179,38,30,.1)')] : ['OFF · RECOMMENDED', chip('#12503C', 'rgba(31,107,82,.14)')],
    },
  ];

  return (
    <>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.55;max-width:92ch;text-wrap:pretty;')}>
        {I(d.canEdit
          ? 'Only you can change these. Each change asks for a reason and your passkey, and is sealed to the Truth Ledger. Changes stay listed below for 24 hours.'
          : 'You can see these but not change them. Only Andrew can.')}
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,400px),1fr));gap:0 30px;align-items:start;margin-top:10px;')}>
        <div style={sx('min-width:0;')}>
          <div style={sx(CAP + 'margin:6px 0 2px;')}>Which model answers</div>
          {MODELS.map((m) => {
            const on = d.model === m.key;
            const c = cand(m.key);
            const locked = m.key !== 'anthropic' && (c?.locked ?? true);
            const exam = c?.exam;
            return (
              <div key={m.key} style={sx(ROW)}>
                <span style={sx(radio(on))} aria-hidden="true" />
                <div style={sx('flex:1 1 220px;min-width:min(100%,220px);')}>
                  <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(m.name)}</span>
                    {on ? <span style={sx(chip('#12503C', 'rgba(31,107,82,.14)'))}>IN USE</span> : locked ? <span style={sx(chip('#54606C', 'rgba(34,39,46,.08)'))}>LOCKED · NOT PASSED</span> : null}
                  </div>
                  <div style={sx('font-size:16px;color:#54606C;margin-top:3px;line-height:1.5;text-wrap:pretty;')}>{I(m.note)}</div>
                  {locked && exam && (
                    <div style={sx('display:flex;gap:14px;flex-wrap:wrap;margin-top:6px;font-size:16px;color:#3C454F;font-variant-numeric:tabular-nums;')}>
                      {([[exam.jobsPassed + ' of ' + exam.jobsTotal, 'jobs passed'], [exam.criticalFailures === null ? 'not run' : String(exam.criticalFailures), 'critical failures'], [Math.min(exam.shadowDays, 14) + ' of 14', 'shadow days']] as [string, string][]).map(([v, k]) => (
                        <span key={k}>
                          <span style={sx('font-weight:700;')}>{v}</span> <span>{k}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {owner && !on && !locked && (
                  <a href="#" onClick={ask('model', m.key, 'Switch Atlas to ' + m.name + '?')} style={sx(BTN)}>Use this</a>
                )}
              </div>
            );
          })}
          {d.model !== 'anthropic' && (
            <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:10px 12px;margin-top:6px;background:#F6E3E1;border-radius:9px;')}>
              <span style={sx('flex:1 1 200px;font-size:16px;color:#8A1C16;line-height:1.45;')}>Atlas is not on Claude right now.</span>
              <a href="#" onClick={(e) => { pre(e); if (owner) void save({ key: 'model', value: 'anthropic', title: 'Back to Claude' }, null); }} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#B3261E;color:#fff;')}>
                Back to Claude
              </a>
            </div>
          )}
          <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;padding:10px 12px;margin-top:8px;background:rgba(47,92,151,.07);border-radius:9px;text-wrap:pretty;')}>
            Resident information stays protected. Mistral’s hosted service never sees it; those questions still go to Claude. Our own server sees it only because it runs inside our BAA. A graduation record is not trusted until the candidate has actually been run.
          </div>
          <div style={sx(CAP + 'margin:16px 0 2px;')}>Shadow model</div>
          <div style={sx('font-size:16px;color:#54606C;line-height:1.5;padding:4px 2px 8px;text-wrap:pretty;')}>Answers the same questions in the background so we can compare. Its answers are never shown or acted on.</div>
          <div role="radiogroup" aria-label="Shadow model" style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
            {SHADOW.map(([k, n]) => (
              <a key={k} href="#" role="radio" aria-checked={d.shadow === k} aria-disabled={!owner || undefined} onClick={d.shadow === k ? (e) => pre(e) : ask('shadow', k, 'Set the shadow model to ' + n + '?')} style={sx(pill(d.shadow === k, !d.canEdit))}>
                {n}
              </a>
            ))}
          </div>
          {/* V6: what the shadow does right now, in words (asShadowState / asShadowNote). */}
          <div style={sx('font-size:16px;line-height:1.5;padding:8px 2px 0;color:#3C454F;text-wrap:pretty;')}>
            <b>{d.shadow === 'off' ? 'Shadow is off.' : d.shadow === 'mistral' ? 'Shadow is on: Mistral.' : 'Shadow is on: our own model.'}</b>{' '}
            {d.shadow === 'off'
              ? 'Nothing is paired for training.'
              : d.shadow === 'mistral'
                ? 'Each Claude answer to a question with no resident information is paired with Mistral’s for training. Questions with resident information are never sent to Mistral.'
                : 'Our own server answers the same questions inside our BAA, for comparison only.'}
          </div>
          {d.canEdit && <OwnModelExam d={d} />}
        </div>
        <div style={sx('min-width:0;')}>
          <div style={sx(CAP + 'margin:6px 0 2px;')}>Switches</div>
          {toggles.map((t) => (
            <div key={t.key} style={sx(ROW)}>
              <div style={sx('flex:1 1 220px;min-width:min(100%,220px);')}>
                <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(t.name)}</span>
                  <span style={sx(t.tag[1])}>{I(t.tag[0])}</span>
                </div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:3px;line-height:1.5;text-wrap:pretty;')}>{I(t.note)}</div>
              </div>
              <a href="#" role="switch" aria-checked={t.on} aria-label={t.name} aria-disabled={!owner || undefined} onClick={ask(t.key, !t.on, (t.on ? 'Turn off ' : 'Turn on ') + t.name.toLowerCase() + '?')} style={sx(track(t.on, !d.canEdit))}>
                <span style={sx(knob(t.on))} />
              </a>
            </div>
          ))}
          <div style={sx(ROW)}>
            <div style={sx('flex:1 1 220px;min-width:min(100%,220px);')}>
              <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Autonomy level</span>
                {level && <span style={sx('display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;color:#2F5C97;background:rgba(47,92,151,.1);')}>{I(level.toUpperCase())}</span>}
              </div>
              <div style={sx('font-size:16px;color:#54606C;margin-top:3px;line-height:1.5;text-wrap:pretty;')}>Set on the Atlas Autonomy tab, so there is one place to change it.</div>
            </div>
            <a href="#" onClick={(e) => { pre(e); navigate('/atlas?tab=autonomy'); }} style={sx(BTN)}>Open Autonomy →</a>
          </div>
          <div style={sx(CAP + 'margin:16px 0 2px;')}>Limits, not decided yet</div>
          {d.limits.map((l) => (
            <div key={l.key} style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;padding:8px 2px;border-top:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('flex:1 1 200px;font-size:16px;color:#3C454F;')}>{I(LIMIT_LABEL[l.key] ?? l.key)}</span>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;font-variant-numeric:tabular-nums;')}>{I(l.value)}</span>
              <span style={sx('font-size:16px;color:#54606C;')}>{I(l.note)}</span>
            </div>
          ))}
        </div>
      </div>
      {owner && pend && (
        <div style={sx('display:flex;flex-direction:column;gap:8px;margin-top:14px;padding:12px 14px;border:1px solid rgba(47,92,151,.3);border-radius:10px;background:#FCFAF4;')}>
          <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(pend.title)}</div>
          <input type="text" aria-label="Reason" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Reason (required, goes on the Truth Ledger)" style={sx('height:34px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;font-size:16px;font-family:inherit;background:#FBF8F1;box-sizing:border-box;')} />
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;align-items:center;')}>
            <a href="#" aria-disabled={!why.trim() || undefined} onClick={(e) => { pre(e); if (why.trim()) void save(pend, why.trim()); }} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (why.trim() ? 'background:#264C7E;color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;cursor:default;'))}>
              Confirm with Face ID or passkey
            </a>
            <a href="#" onClick={(e) => { pre(e); setPend(null); setWhy(''); setErr(null); }} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#54606C;font-weight:600;')}>
              Cancel
            </a>
          </div>
        </div>
      )}
      <ErrLine error={err} style="margin-top:8px;" />
      <div style={sx(CAP + 'margin:16px 0 4px;')}>Changed in the last 24 hours</div>
      {d.log.map((g, i) => (
        <div key={i} style={sx('display:flex;gap:10px;flex-wrap:wrap;padding:7px 2px;border-top:1px solid rgba(34,39,46,.08);font-size:16px;color:#3C454F;')}>
          <span style={sx('font-weight:600;')}>{I(label(g.key, String(g.value)))}</span>
          <span style={sx('color:#54606C;')}>{I((g.actorName ?? 'Andrew') + ' · ' + new Date(g.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) + (g.gated ? ' · passkey · ' + (g.reason ?? '') : ' · one tap, no gate') + ' · ledger')}</span>
        </div>
      ))}
      {d.log.length === 0 && (
        <div style={sx('font-size:16px;color:#54606C;padding:7px 2px;border-top:1px solid rgba(34,39,46,.08);')}>No changes. Every change needs a reason and a passkey, and stays listed here for 24 hours.</div>
      )}
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;padding-top:10px;text-wrap:pretty;')}>Crisis and medical questions always follow the fixed routing. Nothing on this page can change that.</div>
    </>
  );
}

/** The six jobs the exam scores (Console V6 asExam): what Atlas does, and for whom. */
const EXAM_JOBS: [string, string][] = [
  ['Summarize a shift handoff', 'Care Staff'],
  ['Draft a family update', 'Facility Admin'],
  ['Sort a care request by urgency', 'Care Staff'],
  ['Summarize a Pulse incident', 'Aithora team'],
  ['Draft a sales follow-up', 'Sales'],
  ['Search the facility catalog', 'Sales'],
];

/**
 * "Own model progress" (Console V6, owner only): the exam against Claude, job by job. The
 * server reports each candidate's totals, not per job, so a job reads "not run" until a
 * candidate has been run at all, and otherwise the totals are said in words.
 */
function OwnModelExam({ d }: { d: AssistantAdminSettings }) {
  const local = d.candidates.find((c) => c.key === 'local');
  const mistral = d.candidates.find((c) => c.key === 'mistral');
  const ran = (c?: AssistantAdminSettings['candidates'][number]) => !!c && (c.exam.criticalFailures !== null || c.exam.jobsPassed > 0);
  const moved = d.candidates.filter((c) => !c.locked).length;
  const cell = (c?: AssistantAdminSettings['candidates'][number]) => (ran(c) ? c!.exam.jobsPassed + ' of ' + c!.exam.jobsTotal : 'not run');
  return (
    <div data-screen-label="Own model progress" data-testid="own-model-exam" style={sx('margin-top:14px;padding:10px 12px;border:1px solid rgba(34,39,46,.14);border-radius:10px;background:#FFFFFF;display:flex;flex-direction:column;gap:4px;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Exam against Claude &middot; owner only</span>
        <span style={sx('flex:1 1 auto;')} />
        <span style={sx('font-size:16px;color:#54606C;')}>{moved ? `Moved to our model: ${moved}` : 'Moved to our model: none yet'}</span>
      </div>
      <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) 64px 64px 64px;gap:6px 10px;font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;padding:6px 0 2px;border-bottom:1px solid rgba(34,39,46,.1);')}>
        <span>JOB</span>
        <span style={sx('text-align:right;')}>CLAUDE</span>
        <span style={sx('text-align:right;')}>OURS</span>
        <span style={sx('text-align:right;')}>MISTRAL</span>
      </div>
      {EXAM_JOBS.map(([job, who]) => (
        <div key={job} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 64px 64px 64px;gap:6px 10px;align-items:baseline;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.06);font-size:16px;')}>
          <span style={sx('min-width:0;')}>
            <span style={sx('font-weight:600;color:#22272E;')}>{job}</span>
            <span style={sx('display:block;font-size:16px;color:#54606C;')}>{who}</span>
          </span>
          <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-weight:700;')}>baseline</span>
          <span style={sx('text-align:right;color:#54606C;')}>{ran(local) ? '–' : 'not run'}</span>
          <span style={sx('text-align:right;color:#54606C;')}>{ran(mistral) ? '–' : 'not run'}</span>
        </div>
      ))}
      {/* V9: the explainer opens on the "i" (design k10575). */}
      <AboutThis inline textStyle="font-size:16px;color:#54606C;line-height:1.45;padding-top:4px;">
        {ran(local) || ran(mistral)
          ? `A job moves to our model only after it matches Claude with zero critical failures, then 14 days in shadow. Our own model: ${cell(local)} jobs passed. Mistral: ${cell(mistral)} jobs passed.`
          : 'A job moves to our model only after it matches Claude with zero critical failures, then 14 days in shadow. Nothing has been run yet, so nothing is shown as a score.'}
      </AboutThis>
    </div>
  );
}

